import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of as observableOf } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { ADMIN_ENDPOINT_PREFIX } from '../../constants';
import { MerchantBalanceModel } from '../../models/api/merchant-balance.model';
import { ApiService } from '../../services/api.service';
import { MessageService } from '../../services/message.service';
import { QueryParams, queryParams } from '../../utils';
import {
  getMerchantBalanceForAssetAction,
  getMerchantBalanceForAssetErrorAction,
  getMerchantBalanceForAssetSuccessAction,
  getMerchantBalancesAction,
  getMerchantBalancesErrorAction,
  getMerchantBalancesSuccessAction,
} from './merchant-balance.actions';

const MERCHANT_BALANCE_ENDPOINT = '/v3/balances';
const ADMIN_BALANCE_ENDPOINT = `/${ADMIN_ENDPOINT_PREFIX}/merchants/{merchantId}/balances`;
const ADMIN_BALANCE_FOR_ASSET_ENDPOINT = `/${ADMIN_ENDPOINT_PREFIX}/merchants/{merchantId}/balances/{asset}`;

@Injectable()
export class MerchantBalanceEffect {
  private readonly api = inject(ApiService);
  private readonly actions$ = inject(Actions);
  private readonly messageService = inject(MessageService);

  getDetailBalances$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getMerchantBalancesAction),
      mergeMap(({ merchantId }) => {
        return this.api.get(this.url(merchantId)).pipe(
          map((merchantBalances) => getMerchantBalancesSuccessAction({ merchantBalances })),
          catchError((errors) => observableOf(getMerchantBalancesErrorAction(errors))),
        );
      }),
    ),
  );

  getBalanceForAsset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getMerchantBalanceForAssetAction),
      mergeMap(({ merchantId, asset }) => {
        return this.api.get(this.urlGetBalanceForAsset(merchantId, asset, { refresh: true })).pipe(
          map((assetBalance: MerchantBalanceModel) => getMerchantBalanceForAssetSuccessAction({ assetBalance })),
          catchError((errors) => observableOf(getMerchantBalanceForAssetErrorAction(errors))),
        );
      }),
    ),
  );

  showSuccessMessageOnGetBalanceForAsset = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getMerchantBalanceForAssetSuccessAction),
        tap(() => {
          this.messageService.success(`Balance was successfully refreshed.`);
        }),
      ),
    { dispatch: false },
  );

  showErrorMessageOnGetBalanceForAsset = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getMerchantBalanceForAssetErrorAction),
        tap(() => {
          this.messageService.success(`Could not refresh the balance.`);
        }),
      ),
    { dispatch: false },
  );

  url(merchantId: string): string {
    if (merchantId) {
      return ADMIN_BALANCE_ENDPOINT.replace('{merchantId}', merchantId);
    }
    return `${MERCHANT_BALANCE_ENDPOINT}`;
  }

  urlGetBalanceForAsset(merchantId: string, assetCode: string, params?: QueryParams): string {
    return `${ADMIN_BALANCE_FOR_ASSET_ENDPOINT.replace('{merchantId}', merchantId).replace('{asset}', assetCode)}${queryParams(params)}`;
  }
}
